import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizard } from '../WizardProvider';
import WizardShell from '../WizardShell';
import ProductAccordionTree from '../components/ProductAccordionTree';
import { buildProductTree } from '../components/productTree';
import { LOS_FIELDS } from '../components/losFields';
import { LOS_LABELS, USER_ROLE_LABELS } from '../types';
import { stateName } from '../usStates';
import {
  MISSING_DATA_TITLE,
  MISSING_DATA_BODY,
  MISSING_DATA_CONFIRM,
  MISSING_DATA_CANCEL,
  SETUP_SUCCESS_NOTICE,
} from '../copy';
import WizardDialog from '../components/WizardDialog';
import { useProducts } from '../../../../hooks/useProducts';
import { useSubmitOnboarding } from '../../../../hooks/useLenderOnboarding';
import { buildOnboardingPayload } from '../../../../api/lenderOnboarding';

const EMPTY = '—';
export const MASKED_VALUE = '••••••••';

function display(value: string, masked: boolean): string {
  if (!value) return EMPTY;
  return masked ? MASKED_VALUE : value;
}

function Field({ label, value, masked = false }: { label: string; value: string; masked?: boolean }) {
  return (
    <div>
      <p className="label">{label}</p>
      <p className="text-gray-900 dark:text-gray-100">{display(value, masked)}</p>
    </div>
  );
}

function SectionHeader({
  title, editLabel, onEdit,
}: { title: string; editLabel: string; onEdit: () => void }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</h2>
      <button
        type="button"
        className="text-sm text-blue-600 hover:underline"
        aria-label={editLabel}
        onClick={onEdit}
      >
        Edit
      </button>
    </div>
  );
}

export default function Step5Review({ onExit }: { onExit: () => void }) {
  const { state, dispatch } = useWizard();
  const navigate = useNavigate();
  const { data } = useProducts();
  const tree = useMemo(() => buildProductTree(data ?? []), [data]);
  const submitOnboarding = useSubmitOnboarding();

  const editStep = (step: number) => dispatch({ type: 'editFromReview', step });

  const [confirmingMissing, setConfirmingMissing] = useState(false);

  // AC12: a success confirmation, then the maintenance view — the lenders list, where the new
  // account is visible. The confirmation travels in navigation state so the list can show it once.
  const submit = () => {
    submitOnboarding.mutate(buildOnboardingPayload(state, data ?? []), {
      onSuccess: () => navigate('/lenders', { state: { notice: SETUP_SUCCESS_NOTICE } }),
    });
  };

  // What the client confirmed a user may legitimately leave for later: an LOS, the product
  // selection, and the users. The account fields are not checked — Step 1 already blocks on them.
  // `products` keeps the entries it toggled back off, so this asks about the values, not the keys.
  const mlConsumerSelected = state.losIntegrations.some((los) => los.type === 'ml-consumer');
  const mlMortgageSelected = state.losIntegrations.some((los) => los.type === 'ml-mortgage');

  const hasMissingData =
    state.losIntegrations.length === 0 ||
    !Object.values(state.products).some(Boolean) ||
    state.users.length === 0;

  const completeSetup = () => (hasMissingData ? setConfirmingMissing(true) : submit());

  return (
    <WizardShell
      title="Summary"
      canAdvance={false}
      onExit={onExit}
      primaryAction={{ label: 'Complete Setup', onClick: completeSetup, disabled: submitOnboarding.isPending }}
    >
      <section className="card p-4">
        <SectionHeader title="Lender Details" editLabel="Edit Lender Details" onEdit={() => editStep(1)} />
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Primary Information</h3>
        <div className="grid gap-4 sm:grid-cols-3 mt-2">
          <Field label="Lender Name" value={state.account.lenderName} />
          <Field label="Salesforce ID" value={state.account.salesforceId} />
          <Field label="NMLS ID" value={state.account.nmlsId} />
        </div>
        {/* The ticket lists NMLS ID but not the address. Shown anyway: Step 1 requires all four
            address fields, so leaving them out would make the required input the only thing the
            user never gets to check before submitting. */}
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-6">Address</h3>
        {/* Same shape as Step 1, so the summary reads back in the layout it was entered in. */}
        <div className="grid gap-4 sm:grid-cols-3 mt-2">
          <div className="sm:col-span-3">
            <Field label="Address" value={state.account.street} />
          </div>
          <Field label="City" value={state.account.city} />
          <Field label="State" value={stateName(state.account.state)} />
          <Field label="Zip" value={state.account.zip} />
        </div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-6">FC1 Credentials</h3>
        <div className="grid gap-4 sm:grid-cols-2 mt-2">
          <Field label="Entity ID" value={state.account.entityId} />
          <Field label="Entity Name" value={state.account.entityName} />
          <Field label="API Username" value={state.account.apiUsername} />
          <Field label="API Password" value={state.account.apiPassword} masked />
        </div>
      </section>

      <section className="card p-4 mt-4">
        <SectionHeader title="LOS Integration" editLabel="Edit LOS Integration" onEdit={() => editStep(2)} />
        {state.losIntegrations.map((los, i) => (
          <div key={i} className={i > 0 ? 'mt-6' : ''}>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{LOS_LABELS[los.type]}</h3>
            <div className="grid gap-4 sm:grid-cols-2 mt-2">
              {/*
                Sourced entirely from LOS_FIELDS' own `masked` flag (AC5: Client Secret, API
                Token, API Password all carry it there) — no label matching here, so renaming a
                label can never silently stop a secret from being masked.
              */}
              {LOS_FIELDS[los.type].map((f) => (
                <Field
                  key={f.key}
                  label={f.label}
                  value={los.credentials[f.key] ?? ''}
                  masked={f.masked}
                />
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="card p-4 mt-4">
        <SectionHeader title="Products" editLabel="Edit Products" onEdit={() => editStep(3)} />
        <ProductAccordionTree tree={tree} selected={state.products} onToggle={() => {}} readOnly />
      </section>

      <section className="card p-4 mt-4">
        <SectionHeader title="Team Members" editLabel="Edit Team Members" onEdit={() => editStep(4)} />
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="table-head">
                <th className="px-3 py-3 text-xs font-semibold text-white">Name</th>
                {/* The column exists only while the integration that feeds it does (AC7.1/7.2). */}
                {mlConsumerSelected && (
                  <th className="px-3 py-3 text-xs font-semibold text-white">MeridianLink Consumer User Name</th>
                )}
                {mlMortgageSelected && (
                  <th className="px-3 py-3 text-xs font-semibold text-white">MeridianLink Mortgage User Name</th>
                )}
                <th className="px-3 py-3 text-xs font-semibold text-white">Email</th>
                <th className="px-3 py-3 text-xs font-semibold text-white">Role</th>
                <th className="px-3 py-3 text-xs font-semibold text-white">Products</th>
              </tr>
            </thead>
            <tbody>
              {state.users.map((user) => (
                <tr key={user.id} className="border-t border-gray-200 dark:border-gray-700">
                  {/* One Name column, like the design and the Step 4 table. */}
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                    {`${user.firstName} ${user.lastName}`.trim() || EMPTY}
                  </td>
                  {mlConsumerSelected && (
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      {user.mlConsumerUserName || EMPTY}
                    </td>
                  )}
                  {mlMortgageSelected && (
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      {user.mlMortgageUserName || EMPTY}
                    </td>
                  )}
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{user.email || EMPTY}</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                    {user.role ? USER_ROLE_LABELS[user.role] : EMPTY}
                  </td>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                    {user.productAccess === null ? 'All' : 'Edited'}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      </section>

      {submitOnboarding.isError && (
        <p className="text-sm text-red-500 mt-4">{(submitOnboarding.error as Error).message}</p>
      )}

      {/* Confirms rather than blocks: a lender created with partial data is a supported outcome,
          and it is created active either way. Cancelling only closes the dialog — nothing about
          the wizard state is touched, so the LOS selection and everything else survive. */}
      <WizardDialog
        open={confirmingMissing}
        title={MISSING_DATA_TITLE}
        body={MISSING_DATA_BODY}
        confirmLabel={MISSING_DATA_CONFIRM}
        cancelLabel={MISSING_DATA_CANCEL}
        onConfirm={() => { setConfirmingMissing(false); submit(); }}
        onCancel={() => setConfirmingMissing(false)}
      />
    </WizardShell>
  );
}
